import { Wifi, WifiOff } from "lucide-react"
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useFleetHealth } from "@/hooks/use-fleet-health"

interface FleetHealthWidgetProps {
  totalDevices?: number
  activeDevices?: number
}

export function FleetHealthWidget({ totalDevices, activeDevices }: FleetHealthWidgetProps) {
  const { total, active, offline, healthPercent, hasData } = useFleetHealth(
    totalDevices,
    activeDevices,
  )

  const allPieItems = [
    { name: "Online", value: active, color: "hsl(var(--chart-2))" },
    { name: "Offline", value: offline, color: "hsl(var(--destructive))" },
  ]
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Fleet Health</CardTitle>
          {hasData && <span className="text-2xl font-bold">{healthPercent}%</span>}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {hasData ? `${active} of ${total} devices online` : "No device data available"}
        </p>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={allPieItems}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                    paddingAngle={0}
                  dataKey="value"
                >
                  {allPieItems.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const entry = payload[0]?.payload
                      if (!entry) return null
                      return (
                        <div className="bg-card border rounded-lg p-3 shadow-lg">
                          <p className="font-medium">{entry.name}</p>
                          <p style={{ color: entry.color }}>{entry.value} devices</p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Legend
                  formatter={(value) => (
                    <span className="text-sm text-muted-foreground">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
            <WifiOff className="size-12 mb-3 opacity-30" />
            <p className="text-sm">No data available</p>
          </div>
        )}
        <div className="mt-4 pt-4 border-t space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Wifi className="size-4 text-chart-2" /> Online
            </span>
            <span className="font-semibold" style={{ color: "hsl(var(--chart-2))" }}>
              {active}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-muted-foreground">
              <WifiOff className="size-4 text-destructive" /> Offline
            </span>
            <span className="font-semibold text-destructive">{offline}</span>
          </div>
          <div className="flex items-center justify-between text-sm pt-1 border-t">
            <span className="text-muted-foreground">Total Devices</span>
            <span className="font-semibold">{total}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
